import { Component } from "react";
import { connect } from "react-redux";
import { Dispatch, bindActionCreators } from "redux";
import { History } from "history";
import moment from "moment";
import {
  DefaultButton,
  DialogFooter,
  Dropdown,
  Icon,
  Label,
  Panel,
  PrimaryButton,
  TextField,
  Toggle,
} from "office-ui-fabric-react";

import { RootState } from "app/redux/reducer";
import { setModalData, toggleModal } from "app/redux/modal/actions";
import { castThunkAction } from "app/utils/casting";
import { showMessage, toggleConfirmDialog } from "app/redux/system/actions";
import {
  ActivityVariantStatus,
  createActivityVariant,
  getActivityVariantByLanguage,
  updateActivityVariant,
  getActivityVariant,
  IActivityVariant,
  deleteActivityVariant,
  IActivityVariantArtist,
} from "app/services/activity-variant";
import { getConfiguration, IThemeSettings } from "app/services/configuration";
import { getArtistsForLocal, IArtist } from "app/services/artist";
import { getArtistRolesForLocal, IArtistRole } from "app/services/artist-role";
import { getPlacesForLocal, IPlace } from "app/services/place";

import AbsoluteLoader from "../Loader/AbsoluteLoader";
import { TranslatorPostBar } from "../TranslatorBar";
import ArtistPicker from "./Operations/ArtistPicker";

interface OwnProps {
  history: History<any>;
}

function mapStateToProps(state: RootState) {
  return {
    ...state.modal.activityVariant,
    defaultLanguage: state.system.defaultLanguage,
    language: state.system.language,
    modalLoading: state.system.modalLoading,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    ...bindActionCreators(
      {
        setModalData,
        toggleModal,
        showMessage,
        createActivityVariant,
        updateActivityVariant,
        getActivityVariant,
        getActivityVariantByLanguage,
        toggleConfirmDialog,
        getArtistsForLocal,
        getPlacesForLocal,
        getArtistRolesForLocal,
        getConfiguration,
        deleteActivityVariant,
      },
      dispatch
    ),
  };
}

type ActivityVariantProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  OwnProps;

interface ActivityVariantState {
  showModal: boolean;
  configurationData: IThemeSettings;
  artists: IArtist[];
  artistRoles: IArtistRole[];
  places: IPlace[];
}

class ActivityVariantModal extends Component<
  ActivityVariantProps,
  ActivityVariantState
> {
  state = {
    showModal: false,
    configurationData: {} as IThemeSettings,
    artists: [] as IArtist[],
    artistRoles: [] as IArtistRole[],
    places: [] as IPlace[],
  };

  UNSAFE_componentWillReceiveProps(nextProps: ActivityVariantProps) {
    if (nextProps.show !== this.props.show && nextProps.show) {
      this.makeRequests();
    }
  }

  makeRequests() {
    castThunkAction<IThemeSettings>(this.props.getConfiguration(true)).then(
      (configuration) => {
        if (configuration) {
          this.setState({
            configurationData: configuration,
          });
        }

        this.getArtists();
        this.getArtistRoles();
        this.getPlaces();
      }
    );
  }

  getArtists(language?: string) {
    castThunkAction<IArtist[]>(
      this.props.getArtistsForLocal(
        language || this.props.data.language || "tr"
      )
    ).then((response) => {
      this.setState({
        artists: response || [],
      });
    });
  }

  getArtistRoles(language?: string) {
    castThunkAction<IArtistRole[]>(
      this.props.getArtistRolesForLocal(
        language || this.props.data.language || "tr"
      )
    ).then((response) => {
      this.setState({
        artistRoles: response || [],
      });
    });
  }

  getPlaces(language?: string) {
    castThunkAction<IPlace[]>(
      this.props.getPlacesForLocal(language || this.props.data.language || "tr")
    ).then((response) => {
      this.setState({
        places: response || [],
      });
    });
  }

  onClose(updated?: boolean) {
    if (this.props.callback) {
      this.props.callback(updated);
    }
    this.props.toggleModal("activityVariant", {});
  }

  onSave() {
    if (this.props.data.endDate && this.props.data.startDate) {
      const startDate = moment(this.props.data.startDate);
      const endDate = moment(this.props.data.endDate);
      const daysDiff = endDate.diff(startDate, "days");

      console.log("burdayız", daysDiff);

      if (daysDiff > 0) {
        // Store the current data for use after rejection
        const currentData = this.props.data;

        this.props.toggleConfirmDialog(true, {
          title: "Çoklu Seans Ekleme",
          text: `Bu tarih aralığı içinde ${
            daysDiff + 1
          } adet seans oluşturulacak. Devam etmek istiyor musunuz?`,
          button: {
            approveText: "Evet Oluştur",
            cancelText: "Hayır, tek seans oluştur",
          },
          callback: () => {
            const promises: Promise<any>[] = [];
            for (let i = 0; i <= daysDiff; i++) {
              const newStartDate = startDate
                .clone()
                .add(i, "days")
                .format("YYYY-MM-DDTHH:mm");
              const newEndDate = startDate
                .clone()
                .add(i, "days")
                .set({
                  hour: endDate.hour(),
                  minute: endDate.minute(),
                })
                .format("YYYY-MM-DDTHH:mm");

              const newData = {
                ...this.props.data,
                startDate: newStartDate,
                endDate: newEndDate,
              };

              promises.push(
                castThunkAction(this.props.createActivityVariant(newData))
              );
            }

            const executeSequentially = async () => {
              for (const promise of promises) {
                await promise;
                await new Promise((resolve) => setTimeout(resolve, 500)); // 500ms delay between requests
              }
            };

            this.props.setModalData("activityVariant", {
              ...this.props.data,
              loading: true,
            });

            executeSequentially().then(() => {
              this.props.setModalData("activityVariant", {
                ...this.props.data,
                loading: false,
              });
              this.onClose(true);
            });
          },
        });

        // Listen for dialog closing without confirmation
        const checkDialogStatus = setInterval(() => {
          // If the dialog is closed and we still have the same data (meaning it wasn't confirmed)
          if (
            !document.querySelector(".ms-Dialog") &&
            this.props.data === currentData
          ) {
            clearInterval(checkDialogStatus);

            // If user rejects multiple creation, proceed with normal single variant creation
            castThunkAction(
              this.props.data.id
                ? this.props.updateActivityVariant(this.props.data)
                : this.props.createActivityVariant(this.props.data)
            ).then((res) => {
              if (res) {
                this.onClose(true);
              }
            });
          } else if (this.props.data !== currentData) {
            // Dialog was confirmed and data changed
            clearInterval(checkDialogStatus);
          }
        }, 500);

        return;
      }
    }

    castThunkAction(
      this.props.data.id
        ? this.props.updateActivityVariant(this.props.data)
        : this.props.createActivityVariant(this.props.data)
    ).then((res) => {
      if (res) {
        this.onClose(true);
      }
    });
  }

  onDelete() {
    this.props.toggleConfirmDialog(true, {
      callback: () => {
        let data = this.props.data;

        if (this.props.data.language !== this.props.defaultLanguage) {
          castThunkAction<IActivityVariant>(
            this.props.getActivityVariant(
              this.props.data.id as string,
              this.props.defaultLanguage,
              true
            )
          ).then((response) => {
            data = response;

            castThunkAction(
              this.props.deleteActivityVariant(this.props.data.id as string)
            ).then((res) => {
              if (res) {
                this.props.setModalData("activityVariant", data);
              }
            });
          });
        } else {
          castThunkAction(
            this.props.deleteActivityVariant(this.props.data.id as string)
          ).then((res) => {
            if (res) {
              this.onClose(true);
            }
          });
        }
      },
    });
  }

  onChangeData(key: string, value: any) {
    this.props.setModalData("activityVariant", {
      ...this.props.data,
      [key]: value,
    });
  }

  onChangeLanguage(language: string) {
    if (language !== this.props.data.language) {
      castThunkAction(
        this.props.getActivityVariantByLanguage(this.props.data, language)
      ).then(() => {
        this.getArtists(language);
        this.getPlaces(language);
      });
    }
  }

  onCloneLanguage() {
    this.props.getActivityVariantByLanguage(
      this.props.data,
      this.props.data.language,
      true
    );
  }

  renderTranslatorBar() {
    if (
      !this.state.configurationData?.config?.languages ||
      this.state.configurationData?.config?.languages.length <= 1 ||
      !this.props.data.id
    )
      return null;

    return (
      <TranslatorPostBar
        languages={this.state.configurationData?.config?.languages?.map(
          (item) => item.code?.split("-")[0]
        )}
        onChange={(language) => this.onChangeLanguage(language)}
        toggleDialog={this.props.toggleConfirmDialog}
        current={this.props.data.language}
        onClone={() => this.onCloneLanguage()}
        default={this.props.defaultLanguage}
      />
    );
  }

  renderArtists() {
    const artists: IActivityVariantArtist[] = [
      ...this.props.data.artists,
      { id: "", roleId: "", title: "" },
    ];

    return (
      <div className="item-list col-md-12">
        <Label>Sanatçılar</Label>
        <div className="item-list-wrapper">
          {artists.map((artist, index) => {
            return (
              <div key={index} className="row">
                <Dropdown
                  className="custom-dropdown col-md-6"
                  calloutProps={{
                    className: "custom-dropdown-callout",
                  }}
                  label="Sanatçı Rolü"
                  options={this.state.artistRoles.map((artistRole) => ({
                    key: artistRole.id as string,
                    text: artistRole.title,
                  }))}
                  onChange={(_, opt) => {
                    const reference = [...this.props.data.artists];

                    if (!reference[index]) {
                      reference[index] = {
                        id: "",
                        title: "",
                        roleId: opt?.key as string,
                      };
                    } else {
                      reference[index].roleId = opt?.key as string;
                    }

                    this.onChangeData("artists", reference);
                  }}
                  selectedKey={artist?.roleId || ""}
                  disabled={!this.state.artistRoles.length}
                />
                <ArtistPicker
                  className="col"
                  label="Sanatçı"
                  onChange={(values) => {
                    console.log("values", values);
                    const reference = [...this.props.data.artists];

                    if (!reference[index]) {
                      reference[index] = {
                        id: values.id,
                        title: values.title,
                        roleId: "",
                      };
                    } else {
                      reference[index].id = values.id;
                      reference[index].title = values.title;
                    }

                    this.onChangeData("artists", reference);
                  }}
                  data={artist?.id ? artist : null}
                  language={this.props.data.language || "tr"}
                />
                <span
                  className={`remove col ${
                    index !== artists.length - 1 ? "active" : ""
                  }`}
                  onClick={() => {
                    const reference = [...this.props.data.artists];

                    reference.splice(index, 1);
                    this.onChangeData("artists", reference);
                  }}
                >
                  <Icon iconName="ChromeClose" />
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  renderForm() {
    return (
      <div className="modal-form">
        <div className="row reverse-margin">
          <div className="item col-md-6">
            <TextField
              placeholder="Seans Adı"
              value={this.props.data.title || ""}
              label="Seans Adı"
              required
              className="custom-textfield border"
              onChange={(event: any) =>
                this.props.setModalData("activityVariant", {
                  ...this.props.data,
                  title: event.target.value,
                  adminTitle: event.target.value,
                })
              }
            />
          </div>
          <div className="item col-md-6">
            <TextField
              placeholder="Seans Admin Adı"
              value={this.props.data.adminTitle || ""}
              label="Seans Admin Adı"
              className="custom-textfield border"
              onChange={(event: any) =>
                this.onChangeData("adminTitle", event.target.value)
              }
            />
          </div>
          <div className="item col-md-6">
            <Label>Başlangıç Tarihi</Label>
            <input
              className="custom-datetimepicker"
              type="datetime-local"
              value={
                this.props.data.startDate || moment().format("YYYY-MM-DDTHH:mm")
              }
              onChange={(event) =>
                this.onChangeData("startDate", event.target.value)
              }
            />
          </div>
          <div className="item col-md-6">
            <Label>
              Bitiş Tarihi
              <PrimaryButton
                className={`custom-button custom-button-date m-left-auto ${
                  this.props.data.endDate
                    ? "custom-button-red"
                    : "custom-button-green"
                }`}
                onClick={() =>
                  this.onChangeData(
                    "endDate",
                    !this.props.data.endDate
                      ? moment().format("YYYY-MM-DDTHH:mm")
                      : undefined
                  )
                }
                text={this.props.data.endDate ? "Kullanma" : "Kullan"}
              />
            </Label>
            {this.props.data.endDate ? (
              <input
                className="custom-datetimepicker"
                type="datetime-local"
                value={
                  this.props.data.endDate || moment().format("YYYY-MM-DDTHH:mm")
                }
                onChange={(event) =>
                  this.onChangeData("endDate", event.target.value)
                }
              />
            ) : (
              <p style={{ fontSize: 12 }}>Bitiş tarihi kullanılmıyor!</p>
            )}
          </div>
          <div className="item col-md-6">
            <Dropdown
              className="custom-dropdown"
              calloutProps={{
                className: "custom-dropdown-callout",
              }}
              label="Mekan / Salon"
              options={this.state.places.map((place) => ({
                key: place.id as string,
                text: place.title,
              }))}
              onChange={(_, opt) => {
                this.onChangeData("placeId", opt?.key);
              }}
              selectedKey={this.props.data.placeId}
              disabled={!this.state.places.length}
            />
          </div>
          <div className="item col-md-6">
            <TextField
              placeholder="Seans Sırası"
              type="number"
              value={this.props.data.order?.toString() || "0"}
              label="Seans Sırası"
              className="custom-textfield border"
              onChange={(event: any) =>
                this.onChangeData("order", parseInt(event.target.value))
              }
            />
          </div>
          {this.renderArtists()}
          <div className="item col-md-6">
            <Dropdown
              className="custom-dropdown"
              calloutProps={{
                className: "custom-dropdown-callout",
              }}
              label="Durum"
              options={[
                {
                  key: ActivityVariantStatus.Published,
                  text: "Aktif",
                },
                {
                  key: ActivityVariantStatus.Canceled,
                  text: "İptal Edilmiş",
                },
              ]}
              onChange={(_, opt) => {
                this.onChangeData("variantStatus", opt?.key);
              }}
              selectedKey={this.props.data.variantStatus}
            />
          </div>
          <div className="item row col-md-3">
            <Toggle
              className="custom-toggle m-left-auto"
              onChange={(_, checked) => this.onChangeData("publish", checked)}
              checked={this.props.data.publish}
              label="Web'de Gösterilsin Mi?"
              onText="Göster"
              offText="Gizle"
            />
          </div>
          {!this.props.data.id && (
            <div className="item row col-md-3">
              <Toggle
                className="custom-toggle m-left-auto"
                onChange={(_, checked) =>
                  this.onChangeData("isCreateOtherLanguage", checked)
                }
                checked={this.props.data.isCreateOtherLanguage}
                label="Diğer Dillerde Oluşturulsun Mu?"
                onText="Evet"
                offText="Hayır"
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  render() {
    const { data, show, modalLoading } = this.props;

    if (!show) return null;

    return (
      <Panel
        className="preferences-panel medium"
        isOpen={show}
        onDismiss={() => this.onClose()}
        headerText={data.id ? "Seans Düzenle" : "Yeni Seans Ekle"}
        onOuterClick={() => {}}
        onRenderFooter={() => {
          return (
            <DialogFooter>
              <DefaultButton
                onClick={() => this.onClose()}
                text="İptal"
                className="custom-button"
              />
              {data.id && (
                <PrimaryButton
                  onClick={() => this.onDelete()}
                  text="Seans'ı Sil"
                  className="custom-button custom-button-red"
                />
              )}
              <PrimaryButton
                onClick={() => this.onSave()}
                text={data.id ? "Değişiklikleri Kaydet" : "Seans Oluştur"}
                disabled={modalLoading || !data.title ? true : false}
                className="custom-button"
              />
            </DialogFooter>
          );
        }}
      >
        <div className="sub-text">
          Aşağıdaki bilgileri doldurarak belirlediğiniz kriterlerde bir etkinlik
          seansı ekleyin ya da düzenleyin.
        </div>
        <AbsoluteLoader show={modalLoading} />
        {this.renderTranslatorBar()}
        {this.renderForm()}
      </Panel>
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ActivityVariantModal);
